import { useEffect, useMemo, useState } from 'react'
import type { Tag } from '#shared/types'
import type { Project } from '#shared/projects'
import { todayLocal } from '#shared/dates'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import type { TimeEntry } from '#shared/types'
import { LogTimeForm } from '../components/LogTimeForm'
import type { Shortcut } from '../components/RecentEntries'
import { RecentEntries } from '../components/RecentEntries'
import { DatePicker } from '../components/DatePicker'
import { MyWeek } from '../components/MyWeek'

export function Dashboard() {
  const { user } = useAuth()

  // Computed once per mount: the local calendar date, not the UTC one.
  const today = useMemo(() => todayLocal(), [])

  const [date, setDate] = useState(today)
  const [projects, setProjects] = useState<Project[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [recent, setRecent] = useState<TimeEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * A copied entry, and a token that changes on every copy.
   *
   * The token is the form's `key`, so copying the same shortcut twice
   * rebuilds the form both times. Pushing values into a form that owns its
   * own state would mean two places deciding what is in each field; a
   * remount leaves one.
   */
  const [copied, setCopied] = useState<{ shortcut: Shortcut; token: number } | null>(null)

  /**
   * The week grid is the only view of entries on this page, and it fetches
   * its own. Bumping this after logging time is what tells it to refetch.
   */
  const [refreshToken, setRefreshToken] = useState(0)

  // Projects and tags are date-independent, so they load once. Archived rows
  // are included so historic entries still show a name rather than an id; the
  // form filters them back out of what can be picked.
  useEffect(() => {
    Promise.all([api.listProjects(true), api.listTags(true)])
      .then(([nextProjects, nextTags]) => {
        setProjects(nextProjects)
        setTags(nextTags)
      })
      .catch(() => setError('Could not load projects and tags.'))
      .finally(() => setLoading(false))
  }, [])

  // Refetched after every change, since logging time is exactly what makes
  // the shortcuts stale. A failure here leaves the card as it was rather
  // than taking over the page: it is a convenience, not the timesheet.
  useEffect(() => {
    api.listRecentEntries().then(setRecent).catch(() => {})
  }, [refreshToken])

  function handleLogged() {
    setRefreshToken((n) => n + 1)
  }

  return (
    <section>
      <h1>Hi {user?.name.split(' ')[0]}</h1>

      <DatePicker value={date} onChange={setDate} today={today} />

      {error && <p className="error">{error}</p>}

      {!loading && (
        <LogTimeForm
          key={copied?.token ?? 'blank'}
          date={date}
          projects={projects}
          tags={tags}
          initial={copied?.shortcut}
          // The Copy button that filled this in is below the fold.
          announce={Boolean(copied)}
          onLogged={handleLogged}
        />
      )}

      {!loading && (
        <RecentEntries
          entries={recent}
          projects={projects}
          tags={tags}
          onCopy={(shortcut) => setCopied({ shortcut, token: Date.now() })}
        />
      )}

      <MyWeek refreshToken={refreshToken} />
    </section>
  )
}
